import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './users-page.style.js';
import { getUsersByCompany, createUserWithAuth, updateUser } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { UserRole } from '@cmms/models';
import { createUsersTableConfig } from './users-table-config.js';
import { FUNCTION_ENDPOINTS } from '../../../config/firebase-functions.config.js';
import '../user-viewer/user-viewer.js';

interface User {
  id: string;
  model: {
    user_id: string;
    company_id: string;
    name: string;
    email: string;
    role: UserRole;
    created_at: number;
    updated_at: number;
  };
  authData?: {
    lastSignInTime: string | null;
    disabled: boolean;
    emailVerified: boolean;
  };
}

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  company_id: string;
}

@customElement('users-page')
export class UsersPage extends LitElement {
  @property({ type: String })
  companyId = ''; // Will be set by parent component
  
  @property({ type: Object })
  firestore: Firestore = null as any;

  @state() private users: User[] = [];
  @state() private isLoading = true;
  @state() private error: string | null = null;
  @state() private showAddModal = false;
  @state() private selectedUser: User | null = null;
  @state() private showDeleteModal = false;
  @state() private userToDelete: User | null = null;
  @state() private formData: UserFormData = {
    name: '',
    email: '',
    password: '',
    role: UserRole.SHOP_FLOOR,
    company_id: ''
  };
  @state() private dataLoaded = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.companyId && this.firestore && !this.dataLoaded) {
      this.loadData();
    } else if (!this.companyId || !this.firestore) {
      console.warn('Users page: Missing required props', {
        companyId: this.companyId,
        hasFirestore: !!this.firestore
      });
    }
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('companyId') && this.companyId && !this.dataLoaded) {
      this.loadData();
    }
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.error = null;
      
      // Try to call the server endpoint to get users with auth data
      try {
        const response = await fetch(FUNCTION_ENDPOINTS.GET_USERS_WITH_AUTH, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId: this.companyId
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          this.users = result.data as User[];
          this.dataLoaded = true;
          
          return;
        } else {
          console.warn('⚠️ Server endpoint failed, falling back to local API');
        }
      } catch (serverError) {
        // Handle CORS and other network errors gracefully
        if (serverError instanceof Error && serverError.message.includes('CORS')) {
          console.info('ℹ️ CORS issue detected, using local API instead');
        } else {
          console.warn('⚠️ Server endpoint not available, falling back to local API:', serverError instanceof Error ? serverError.message : String(serverError));
        }
      }

      // Fallback to local API if server endpoint fails
      const result = await getUsersByCompany(this.firestore, this.companyId);
      
      if (result.data && Array.isArray(result.data)) {
        // Add empty authData to users for consistency
        this.users = result.data.map(user => ({
          ...user,
          authData: {
            lastSignInTime: null,
            disabled: false,
            emailVerified: false
          }
        })) as User[];
        this.dataLoaded = true;
        
      } else {
        this.error = result.error || 'Failed to load users - no data returned';
        this.users = [];
        this.dataLoaded = true;
      }
    } catch (error) {
      console.error('Error loading users:', error);
      this.error = 'Failed to load users';
      this.users = [];
      this.dataLoaded = true;
    } finally {
      this.isLoading = false;
    }
  }

  private handleAddUser = () => {
    this.formData = {
      name: '',
      email: '',
      password: '',
      role: UserRole.SHOP_FLOOR,
      company_id: this.companyId
    };
    this.selectedUser = null;
    this.showAddModal = true;
  };

  private handleRowClick = (user: User) => {
    this.selectedUser = user;
    const viewer = document.createElement('user-viewer') as any;
    viewer.open = true;
    viewer.user = user;
    viewer.firestore = this.firestore;
    document.body.appendChild(viewer);
    viewer.addEventListener('modal-closed', () => {
      document.body.removeChild(viewer);
    }, { once: true });
    viewer.addEventListener('user-updated', () => {
      this.dataLoaded = false; // Reset flag to allow reload
      this.loadData(); // Reload data after update
    }, { once: true });
  };

  private handleDeleteClick = (user: User) => {
    
    this.userToDelete = user;
    this.showDeleteModal = true;
  };

  private handleFilterChange = (filters: Record<string, any>) => {
    // Handle filter changes if needed
  };

  private handleSort = (column: string, direction: 'asc' | 'desc') => {
    
    
    // Sort the users array based on the column and direction
    this.users.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (column) {
        case 'name':
          aValue = a.model.name?.toLowerCase() || '';
          bValue = b.model.name?.toLowerCase() || '';
          break;
        case 'email':
          aValue = a.model.email?.toLowerCase() || '';
          bValue = b.model.email?.toLowerCase() || '';
          break;
        case 'role':
          aValue = a.model.role?.toLowerCase() || '';
          bValue = b.model.role?.toLowerCase() || '';
          break;
        case 'created_at':
          aValue = a.model.created_at || 0;
          bValue = b.model.created_at || 0;
          break;
        case 'lastSignInTime':
          aValue = a.authData?.lastSignInTime ? new Date(a.authData.lastSignInTime).getTime() : 0;
          bValue = b.authData?.lastSignInTime ? new Date(b.authData.lastSignInTime).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    // Trigger a re-render by updating the users array
    this.users = [...this.users];
  };

  private async createTestUser() {
    if (!this.companyId) {
      console.error('No company ID available for creating test user');
      return;
    }

    const testUserData = {
      name: 'Test User',
      email: 'test@example.com',
      password: 'password123',
      role: UserRole.SHOP_FLOOR,
      company_id: this.companyId
    };

    try {
      const result = await createUserWithAuth(this.firestore, testUserData);
      
      if (result.data) {
        this.dataLoaded = false; // Reset flag to allow reload
        this.loadData(); // Reload the data
      } else {
        console.error('Failed to create test user:', result.error);
      }
    } catch (error) {
      console.error('Error creating test user:', error);
    }
  };

  private async handleSaveUser(event: CustomEvent) {
    try {
      this.isLoading = true;
      this.error = null;

      // Get form data from the event
      const formData = event.detail.formData;

      // Only create new user (no editing in modal)
      const result = await createUserWithAuth(this.firestore, formData);
      
      if (result.data) {
        this.showAddModal = false;
        this.dataLoaded = false; // Reset flag to allow reload
        this.loadData();
      } else {
        this.error = result.error || 'Failed to create user';
      }
    } catch (error) {
      console.error('Error saving user:', error);
      this.error = 'Failed to save user';
    } finally {
      this.isLoading = false;
    }
  }

  private async handleDeleteUser() {
    if (!this.userToDelete) return;

    try {
      this.isLoading = true;
      this.error = null;

      // Call the server endpoint to delete both Auth and Firestore user
      const response = await fetch(FUNCTION_ENDPOINTS.DELETE_USER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: this.userToDelete.id
        })
      });

      const result = await response.json();
      
      if (result.success) {
        this.showDeleteModal = false;
        this.userToDelete = null;
        this.dataLoaded = false; // Reset flag to allow reload
        this.loadData();
        
      } else {
        this.error = result.error || 'Failed to delete user';
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      this.error = 'Failed to delete user';
    } finally {
      this.isLoading = false;
    }
  }

  private handleModalClosed() {
    this.showAddModal = false;
    this.selectedUser = null;
    this.formData = {
      name: '',
      email: '',
      password: '',
      role: UserRole.SHOP_FLOOR,
      company_id: this.companyId
    };
  }

  private handleDeleteModalClosed() {
    this.showDeleteModal = false;
    this.userToDelete = null;
  }


  render() {
    // Show loading if component is not properly initialized
    if (!this.companyId || !this.firestore) {
      return html`
        <div class="users-page">
          <div class="loading-container">
            <sl-spinner></sl-spinner>
            <p>Initializing user management...</p>
          </div>
        </div>
      `;
    }

    const tableConfig = createUsersTableConfig(
      this.users,
      {
        onRowClick: this.handleRowClick,
        onDelete: this.handleDeleteClick,
        onAddUser: this.handleAddUser,
        onFilterChange: this.handleFilterChange,
        onSort: this.handleSort
      }
    );

    // Override loading and error state
    tableConfig.loading = this.isLoading;
    tableConfig.error = this.error;
    
    
    return html`
      <div class="users-page">

        <data-table .config=${tableConfig}></data-table>

        <!-- Add User Modal -->
        <user-modal
          .open=${this.showAddModal}
          .formData=${this.formData}
          .isEditing=${false}
          @modal-closed=${this.handleModalClosed}
          @user-saved=${this.handleSaveUser}
        ></user-modal>

        <!-- Delete Confirmation Modal -->
        <sl-dialog label="Delete User" .open=${this.showDeleteModal} @sl-hide=${this.handleDeleteModalClosed}>
          <p>Are you sure you want to delete <strong>${this.userToDelete?.model.name}</strong>?</p>
          <p class="warning-text">This action cannot be undone.</p>
          
          <div slot="footer">
            <sl-button variant="default" @click=${this.handleDeleteModalClosed}>
              Cancel
            </sl-button>
            <sl-button variant="danger" @click=${this.handleDeleteUser}>
              Delete User
            </sl-button>
          </div>
        </sl-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'users-page': UsersPage;
  }
}
